import {
  Button,
  CheckboxField,
  Flex,
  JaneLogo,
  Link,
  Modal,
  Typography,
} from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import dayjs from 'dayjs';
import noop from 'lodash-es/noop';

import { useSafeState } from '@jane/shared/hooks';
import { monolithPaths as paths } from '@jane/shared/util';

import styles from './ageGateModal.module.css';

interface AgeGateModalProps {
  onConfirm: (type: 'age-gate' | 'med-patient' | 'birth-date') => void;
  rememberMe: boolean;
  setRememberMe: (rememberMe: boolean) => void;
  showAgeGateModal: boolean;
  showBirthDateModal?: boolean;
  state?: string;
}

const PRIMARY_MESSAGE = `Please confirm you're over 21 or a valid medical patient`;
const PRIMARY_MESSAGE_ID = 'primary-message';

const AGE_MESSAGE = 'Verify your age for ';
const AGE_MESSAGE_ID = 'age-message';

const RememberMeCheckbox = ({
  rememberMe,
  setRememberMe,
}: {
  rememberMe: boolean;
  setRememberMe: (rememberMe: boolean) => void;
}) => (
  <Flex justifyContent="center" alignItems="center">
    <CheckboxField
      mt={24}
      checked={rememberMe}
      defaultChecked={false}
      label="Remember me for 30 days"
      name="remember_confirmed_conditions"
      onChange={setRememberMe}
    />
  </Flex>
);

const AgreementLinks = ({ action }: { action: string }) => (
  <Typography mt={24} color="grays-mid" variant="mini" textAlign="center">
    By {action} you agree to Jane&apos;s{' '}
    <Link href={paths.termsOfUse()} color="grays-mid">
      Terms of Service
    </Link>
    {' & '}
    <Link href={paths.privacyPolicy()} color="grays-mid">
      Privacy Policy
    </Link>
  </Typography>
);

export const AgeGateModal = ({
  showAgeGateModal,
  showBirthDateModal,
  onConfirm,
  rememberMe,
  setRememberMe,
  state,
}: AgeGateModalProps) => {
  const [showAgeSelector, setShowAgeSelector] = useSafeState(
    showBirthDateModal && !showAgeGateModal ? true : false
  );

  const showModal = showAgeGateModal || showAgeSelector;

  if (!showModal) return null;

  return (
    <Modal
      contentLabel="Confirm your age"
      open={showModal}
      onRequestClose={noop}
      variant="dialogue"
    >
      <Modal.Content
        className={
          showAgeSelector
            ? styles['modalContentBirthDate']
            : styles['modalContentAgeGate']
        }
        padding={false}
        aria-describedby={showAgeSelector ? AGE_MESSAGE_ID : PRIMARY_MESSAGE_ID}
      >
        {showAgeSelector ? (
          <Form
            name="age_gate"
            onSubmit={() => {
              onConfirm('birth-date');
              setShowAgeSelector(false);
            }}
          >
            <Typography
              branded
              variant="header-bold"
              id={AGE_MESSAGE_ID}
              aria-label={`${AGE_MESSAGE}${state}`}
              textAlign="center"
            >
              {AGE_MESSAGE}
              {state}
            </Typography>
            <Form.DateTimeField
              mt={24}
              name="birth_date"
              label="Date of birth"
              required
              disableMobileInputStyling
              validate={(v: string) => {
                if (dayjs().diff(v, 'years') < 21) {
                  return 'You must be 21 or older to enter';
                }
                return true;
              }}
            />
            <RememberMeCheckbox
              rememberMe={rememberMe}
              setRememberMe={setRememberMe}
            />
            <Button
              mt={24}
              full
              type="submit"
              label="Confirm"
              branded
              data-testid="confirm-birth-date"
            />
            <AgreementLinks action='clicking "Confirm"' />
          </Form>
        ) : (
          <>
            <Flex justifyContent="center" alignItems="center">
              <JaneLogo color="black" variant="primary" size="sm" />
            </Flex>
            <Typography
              mt={4}
              branded
              variant="header-bold"
              id={PRIMARY_MESSAGE_ID}
              aria-label={PRIMARY_MESSAGE}
              textAlign="center"
            >
              {PRIMARY_MESSAGE}
            </Typography>
            <Button
              mt={24}
              full
              onClick={() =>
                showBirthDateModal
                  ? setShowAgeSelector(true)
                  : onConfirm('age-gate')
              }
              label="I'm over 21"
              branded
              data-testid="confirm-age-gate"
            />
            <Button
              mt={8}
              full
              onClick={() => onConfirm('med-patient')}
              label="I'm a valid medical patient"
              branded
              data-testid="confirm-med-patient"
              variant="secondary"
            />
            <RememberMeCheckbox
              rememberMe={rememberMe}
              setRememberMe={setRememberMe}
            />
            <AgreementLinks action="continuing" />
          </>
        )}
      </Modal.Content>
    </Modal>
  );
};
